import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getColumns, AVAILABLE_COLUMNS } from "views/Pages/Finding/constants";
import { rootClasses } from "views/Pages/Finding/styles";
import { findingColumnOptions } from "lib/util/FindingUtils";
import FilterEditor from "views/Components/Reporting/FilterEditor";

const PREFIX = "AdvancedFilters";

const classes = {
  actions: `${PREFIX}-actions`,
};

export const Root = styled("div")(({ theme }) => ({
  marginBottom: "20px",
  [`& .${classes.actions}`]: {
    padding: theme.spacing(0.5),
  },
}));

const AdvancedFilters = ({
  activeFields,
  setActiveFields,
  controls,
  controlInterface,
  onApplyFilters,
  onClearFilters,
}) => {
  const handleAddColumn = (event) => {
    if (
      event.target.value &&
      activeFields.length !== AVAILABLE_COLUMNS.length
    ) {
      setActiveFields((prev) => [...prev, event.target.value.field]);
    }
  };

  const handleRemoveColumn = (field) => {
    if (activeFields.length > 1) {
      setActiveFields(activeFields.filter((f) => f !== field));
    }
  };

  const handleDistinctChange = (event) => {
    const existingCount = Object.values(controls).find(
      (param) => param.field === "count" || param.operator === "count"
    );
    if (isDistinctChecked()) {
      controlInterface.remove(existingCount?.uuid);
    } else {
      controlInterface.addOrUpdate({
        field: "count",
        value: "grouped_count",
        uuid: existingCount?.uuid,
      });
    }
  };

  const isDistinctChecked = () => {
    for (const control of Object.values(controls)) {
      if (control.field === "count" || control.operator === "count") {
        return true;
      }
    }
    return false;
  };

  const filterActions = [
    {
      label: "Done",
      onClick: controlInterface.addOrUpdate,
    },
    {
      label: "Cancel",
      onClick: (params) => {
        // noop.  Just closes editor
      },
    },
  ];

  return (
    <div className={rootClasses.filterContainer}>
      <div className={rootClasses.filterSectionOuterContainer}>
        <div className={rootClasses.advancedSectionContainer}>
          <FormControl datacy="showColumnsFormControl">
            <InputLabel id="select-label">Show columns</InputLabel>
            <Select
              datacy="showColumnsSelect"
              value={activeFields}
              onChange={handleAddColumn}
              labelId={"select-label"}
              label={"Show columns"}
              renderValue={() => (
                <div>
                  {getColumns(activeFields).map((column) => (
                    <Chip
                      clickable
                      color={"primary"}
                      datacy={`columnChip-${column.field}`}
                      key={column.title}
                      label={column.title}
                      className={rootClasses.chip}
                      onMouseDown={(event) => {
                        event.stopPropagation();
                      }}
                      onDelete={() => handleRemoveColumn(column.field)}
                    />
                  ))}
                </div>
              )}
            >
              {AVAILABLE_COLUMNS.filter(
                (c) => !activeFields.includes(c.field)
              ).map((column) => (
                <MenuItem
                  datacy={`columnOption-${column.field}`}
                  key={column.title}
                  value={column}
                >
                  {column.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className={rootClasses.advancedSectionContainer}>
          <p className={rootClasses.titleText}>Filters</p>
          <FilterEditor
            actions={filterActions}
            controls={controls}
            fields={findingColumnOptions}
            interface={controlInterface}
            namespace="/finding"
          />
        </div>
        <div className={rootClasses.distinctCountContainer}>
          <Switch
            color={"primary"}
            datacy="distinctCountSwitch"
            onChange={handleDistinctChange}
            checked={isDistinctChecked()}
          />
          <p className={rootClasses.sectionText}>Apply Distinct Count</p>
        </div>
      </div>
      <div className={rootClasses.buttonContainer}>
        <Button
          color={"primary"}
          datacy="applyFiltersButton"
          variant={"contained"}
          onClick={onApplyFilters}
        >
          Apply
        </Button>
        <Button
          color={"primary"}
          datacy="clearFiltersButton"
          onClick={onClearFilters}
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

AdvancedFilters.propTypes = {
  controlInterface: PropTypes.object.isRequired,
  controls: PropTypes.object.isRequired,
};

export default AdvancedFilters;
